import { TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { authService } from 'v2/shared/services/auth-service'
import { hodorService } from 'v2/shared/services/hodor-service'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { isEmbedMode, isSubAccountEmbedSession } from 'v2/shared/utils/embed-session'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { useAppStore } from 'v2/state/app'
import { PlanType } from 'v2/types'
import type { RLoginResponse, RUserSession } from 'v2/types/api/auth'
import type {
  RAccountConcurrency,
  RBusinessHoursPayload,
  RChannelConfiguration,
  RConversationSettings,
  RDestination,
  RDestinationsList,
  RFlows,
  RKeyContacts,
  RKeyContactsPayload,
  RNonTeamMembersList,
  RProfilePermissions,
  RQueue,
  RRolesList,
  RRoutingSettings,
  RSkill,
  RSkillsList,
  RSwitchOrganizationPayloadV2,
  RTeamsList,
  RTimezonesList,
  RUsageLimit,
  RUserProfile,
  RUserProfilePayload,
  RUsersList,
  UsageLimitPayload,
} from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { errorToast, successToast } from 'v2/utils/toasts'
import { createUserSession } from '../auth/utils/session-utils'
import { StartNodeTypes } from '../flow/types'
import { orgSwitchReloadTarget } from './org-switch-reload-target'
import {
  buildAvailableNumbersParams,
  type Capability,
  DEFAULT_SERVICES,
} from './phone-numbers/buy-number/search-params'
import type { HolidayFormFields, UpsertDomainPayload } from './types'

export interface PaginationParams {
  limit: number
  page: number
}

export const useBusinessHoursMutation = (uuid?: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: RBusinessHoursPayload) =>
      settingsService.updateBusinessHours(uuid || '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().businessHours })
    },
  })
}
export const useBusinessHoursDeleteMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => settingsService.deleteBusinessHours(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().businessHours, exact: true })
    },
  })
}
export const useBusinessHourDetailsQuery = (uuid: string) =>
  useQuery({
    queryKey: [...getSettingsQueryKeys().businessHours, uuid],
    queryFn: async () => {
      const response = await settingsService.getBusinessHourDetails(uuid)
      return response.data
    },
  })

export const useAvailableNumbersQuery = (
  country: string,
  search: string,
  numberType: string,
  options?: {
    limit?: number
    offset?: number
    useInfinite?: boolean
    numberStartswith?: string
    services?: Capability[]
  },
) => {
  const {
    limit = 20,
    useInfinite = false,
    numberStartswith,
    services = DEFAULT_SERVICES,
  } = options || {}
  const servicesKey = services.join(',')

  if (useInfinite) {
    return useInfiniteQuery({
      queryKey: [
        ...getSettingsQueryKeys().availableNumbers(country, search, numberType),
        limit,
        numberStartswith ?? '',
        servicesKey,
      ],
      staleTime: convertToMs(2, TimeUnits.Minutes),
      queryFn: async ({ pageParam = 0 }) => {
        const params = buildAvailableNumbersParams({
          country,
          search,
          services,
          numberType,
          limit,
          offset: pageParam,
          numberStartswith,
        })
        const response = await settingsService.searchAvailableNumbers(params)
        return response.data
      },
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < limit) return undefined // If we got fewer results than the limit, we've reached the end
        return allPages.length * limit // Return the next offset
      },
      initialPageParam: 0,
      select: (data) => data.pages.flat(),
      enabled: !!country,
    })
  }

  return useQuery({
    queryKey: [
      ...getSettingsQueryKeys().availableNumbers(country, search, numberType),
      numberStartswith ?? '',
      servicesKey,
    ],
    staleTime: convertToMs(2, TimeUnits.Minutes),
    queryFn: async () => {
      const params = buildAvailableNumbersParams({
        country,
        search,
        services,
        numberType,
        numberStartswith,
      })
      const response = await settingsService.searchAvailableNumbers(params)
      return response.data
    },
    enabled: !!country,
  })
}

export const useFlowsQuery = <TSelect = RFlows>(
  select?: (data: RFlows[]) => TSelect,
  type?: string,
) =>
  useQuery<RFlows[], unknown, TSelect>({
    queryKey: getSettingsQueryKeys().flowsByType(type),
    queryFn: async () => {
      const response = await settingsService.listFlowsByType(type)
      return response.data
    },
    select,
  })

/** Slim single-flow lookup via /phlo/options?uuid= (SER-5833) — e.g. to read the
 * assigned flow's state without fetching the whole flows list. */
export const useFlowOptionLookupQuery = (uuid?: string) =>
  useQuery({
    queryKey: [...getSettingsQueryKeys().flowOptionsBase, 'lookup', uuid],
    queryFn: () => settingsService.listFlowOptions({ uuid }),
    enabled: !!uuid,
    select: (res) => res.data.objects[0],
  })

export const useQueuesQuery = <TSelect = RQueue[]>(select?: (data: RQueue[]) => TSelect) =>
  useQuery<RQueue[], unknown, TSelect>({
    queryKey: getSettingsQueryKeys().queues,
    queryFn: async () => {
      const response = await settingsService.listQueues()
      return response.data
    },
    select,
    // Org-wide; hodor refuses it to sub-account embed sessions.
    enabled: !isSubAccountEmbedSession(),
  })
export const useQueueDetailsQuery = (uuid: string) =>
  useQuery({
    queryKey: [...getSettingsQueryKeys().queues, uuid],
    queryFn: async () => {
      if (!uuid) return null
      const response = await settingsService.getQueueDetails(uuid)
      return response.data
    },
  })

export const useToggleQueueStateMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ queue, state }: { queue: RQueue; state: string }) =>
      settingsService.updateQueueState(queue.uuid, state),
    onSettled: () => queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().queues }),
  })
}
export const useUpsertQueueMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (queue: Partial<RQueue>) => {
      const response = await settingsService.upsertQueue(queue)
      return response
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().queues }),
  })
}
export const useUpdateQueueMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (queue: Partial<RQueue>) => {
      if (!queue.uuid) return null
      const response = await settingsService.updateQueue(queue.uuid, queue)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().queues })
    },
  })
}
export const useDeleteQueueMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => settingsService.deleteQueue(uuid),
    onSettled: () => queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().queues }),
  })
}
export const useTeamsQuery = <TSelect = RTeamsList>(
  select?: (data: RTeamsList) => TSelect,
  enabled = true,
) =>
  useQuery<RTeamsList, unknown, TSelect>({
    queryKey: getSettingsQueryKeys().teams,
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('limit', '200')
      const response = await settingsService.getTeams(Object.fromEntries(params))
      return response.data
    },
    select,
    // Org-wide; hodor refuses it to sub-account embed sessions.
    enabled: enabled && !isSubAccountEmbedSession(),
  })

export const useUsersQuery = <TSelect = RUsersList>(select?: (data: RUsersList) => TSelect) =>
  useQuery({
    queryKey: getSettingsQueryKeys().users,
    meta: { silent: true },
    // org-members is not on the embed gateway allow-list, so it would 403 in embed.
    enabled: !isEmbedMode(),
    queryFn: async () => {
      const response = await settingsService.getUsersList({})
      return response.data
    },
    select,
  })

export const useUpsertSkillMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (skills: RSkill) => {
      const response = await settingsService.upsertSkills(skills)
      return response.data
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().skills }),
  })
}

export const useDeleteSkillMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => {
      const response = await settingsService.deleteSkill(uuid)
      return response.data
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().skills }),
  })
}
export const useSkillsQuery = <TSelect = RSkillsList>(
  select?: (data: RSkillsList) => TSelect,
  enabled = true,
) =>
  useQuery<RSkillsList, unknown, TSelect>({
    queryKey: getSettingsQueryKeys().skills,
    queryFn: async () => {
      const response = await settingsService.getSkills({})
      return response.data
    },
    select,
    // Org-wide; hodor refuses it to sub-account embed sessions.
    enabled: enabled && !isSubAccountEmbedSession(),
  })

export const useRolesQuery = <TSelect = RRolesList>(
  select?: (data: RRolesList) => TSelect,
  enabled = true,
) =>
  useQuery<RRolesList, unknown, TSelect>({
    queryKey: getSettingsQueryKeys().roles,
    queryFn: async () => {
      const response = await settingsService.getRoles()
      return response.data
    },
    select,
    enabled,
  })

export const useNonTeamMembersQuery = <TSelect = RNonTeamMembersList>(
  teamUUID: string | undefined,
  select?: (data: RNonTeamMembersList) => TSelect,
  limit = 100,
) =>
  useQuery<RNonTeamMembersList, unknown, TSelect>({
    queryKey: getSettingsQueryKeys().nonTeamMembersByTeam(teamUUID, { limit, page: 1 }),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (teamUUID) params.append('team_uuid', teamUUID)
      params.append('limit', `${limit}`)
      const response = await settingsService.listNonTeamMembers(Object.fromEntries(params))
      return response.data
    },
    select,
  })

export const useTimezonesQuery = <TSelect = RTimezonesList>(
  select?: (data: RTimezonesList) => TSelect,
) =>
  useQuery<RTimezonesList, unknown, TSelect>({
    queryKey: getSettingsQueryKeys().timezones,
    queryFn: async () => {
      const response = await settingsService.listTimezones()
      return response.data
    },
    select,
  })

// Profile permissions
export const useProfilePermissionQuery = () =>
  useQuery({
    queryKey: getSettingsQueryKeys().profilePermission,
    queryFn: async () => {
      const response = await settingsService.getProfilePermissions()
      return response.data
    },
  })

export const useProfilePermissionsMutation = () => {
  // TODO_V2: Implement onMutate and onError if needed
  // Optimistic updates
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (permissions: RProfilePermissions) => {
      const response = await settingsService.updateProfilePermissions(permissions)
      return response.data
    },
    // !! IMPORTANT: Don't delete this commented code, it's for reference of optimistic updates
    // onMutate: (permissions) => {
    //   const previousData = queryClient.getQueryData<RProfilePermissions>(
    //     getSettingsQueryKeys().profilePermission,
    //   )
    //   queryClient.setQueryData(getSettingsQueryKeys().profilePermission, permissions)
    //   return { previousData }
    // },
    // onError: (_, _variables, context) => {
    //   if (context?.previousData)
    //     queryClient.setQueryData(getSettingsQueryKeys().profilePermission, context.previousData)
    // },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().profilePermission }),
  })
}

// Routing settings
export const useRoutingSettingsQuery = () =>
  useQuery({
    queryKey: getSettingsQueryKeys().routingSettings,
    queryFn: async () => {
      const response = await settingsService.getRoutingSettings()
      return response.data
    },
    staleTime: 0,
  })

export const useRoutingSettingsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (settings: RRoutingSettings) => {
      const response = await settingsService.updateRoutingSettings(settings)
      return response.data
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().routingSettings }),
  })
}

export const useAgentSettingsQuery = () =>
  useQuery({
    queryKey: getSettingsQueryKeys().agentSettings,
    meta: { silent: true },
    // An admin can change the agent's default caller ID at any time; every dialer open re-reads it.
    staleTime: 0,
    queryFn: async () => {
      const response = await settingsService.getAgentSettings()
      return response.data
    },
  })

// Channel configuration
export const useChannelConfigurationQuery = (channel: string) =>
  useQuery({
    queryKey: getSettingsQueryKeys().channelConfigByChannel(channel),
    meta: { silent: true },
    queryFn: async () => {
      const response = await settingsService.getChannelConfiguration(channel)
      return response.data
    },
  })

export const useChannelConfigurationMutation = (channel: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (configuration: RChannelConfiguration) => {
      const response = await settingsService.updateChannelConfiguration(channel, configuration)
      return response.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().channelConfigByChannel(channel),
      })
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().profile })
    },
  })
}

// Helper to sync roles from profile response to session storage
const syncSessionRoles = (profileRoles: string[] | undefined) => {
  if (!Array.isArray(profileRoles) || profileRoles.length === 0) return
  try {
    const authString = localStorage.getItem('auth')
    if (!authString) return
    const auth = JSON.parse(authString)
    if (!auth || typeof auth !== 'object') return
    // Only update if roles have changed
    const currentRoles = JSON.stringify(auth.roles ?? [])
    const newRoles = JSON.stringify(profileRoles)
    if (currentRoles !== newRoles) {
      auth.roles = profileRoles
      localStorage.setItem('auth', JSON.stringify(auth))
    }
  } catch (error) {
    logger.error('Error syncing session roles', error)
  }
}

// Profile API
export const useProfileQuery = () => {
  const { userAuthenticated } = useAuth()
  return useQuery({
    queryKey: getSettingsQueryKeys().profile,
    // /accounts/profile is not on the embed allow-list. This also keeps PubNub inert
    // in embed, since use-pubnub derives its keys from the profile.
    enabled: userAuthenticated && !isEmbedMode(),
    queryFn: async (): Promise<RUserProfile> => {
      const response = await settingsService.getProfile()
      const plan = response.data.organizations?.[0]?.plan ?? PlanType.FREE_TRIAL

      // Sync roles from profile response to session storage
      syncSessionRoles(response.data.organizations?.[0]?.roles)

      return {
        ...response.data,
        plan,
      } as RUserProfile
    },
  })
}

// Account Concurrency API
export const useAccountConcurrencyQuery = (enabled = true) => {
  return useQuery({
    queryKey: getSettingsQueryKeys().accountConcurrency,
    enabled,
    queryFn: async (): Promise<RAccountConcurrency> => {
      const response = await settingsService.getAccountConcurrency()
      return response.data
    },
  })
}

// Account Usage Limit API (account-service)
export const DEFAULT_USAGE_LIMIT_MAX = 1000

export const useUsageLimitQuery = (accountId: string | undefined) => {
  return useQuery({
    queryKey: getSettingsQueryKeys().usageLimit(accountId || ''),
    enabled: !!accountId,
    retry: (failureCount, error) => {
      const status = (error as AxiosError | undefined)?.response?.status
      if (status === 404) return false
      return failureCount < 1
    },
    queryFn: async (): Promise<RUsageLimit | null> => {
      try {
        const response = await settingsService.getUsageLimit(accountId as string)
        return response.data
      } catch (error) {
        const status = (error as AxiosError | undefined)?.response?.status
        if (status === 404) return null
        throw error
      }
    },
  })
}

export const useUpsertUsageLimitMutation = (accountId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { exists: boolean; data: UsageLimitPayload }) => {
      if (!accountId) throw new Error('Account ID is required')
      const response = payload.exists
        ? await settingsService.updateUsageLimit(accountId, payload.data)
        : await settingsService.createUsageLimit(accountId, payload.data)
      return response.data
    },
    onSuccess: () => {
      if (!accountId) return
      queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().usageLimit(accountId),
      })
      successToast({ title: 'Usage limit updated' })
    },
    onError: (error: AxiosError) => {
      errorToast({
        title: 'Could not update usage limit',
        description: getErrorText(error),
      })
    },
  })
}

// List Organizations API
export const useOrganizationsQuery = () => {
  const { userAuthenticated } = useAuth()
  return useQuery({
    queryKey: getSettingsQueryKeys().listOrganizations,
    enabled: userAuthenticated,
    queryFn: async () => {
      if (!getLocalAuth()?.region) return []
      const response = await settingsService.listOrganizations()
      return response.data
    },
    staleTime: 0,
  })
}

// Helper for org switch success handler
const handleOrgSwitchSuccess = async (
  data: RLoginResponse,
  loginSuccess: (userSession: RUserSession) => void,
  logoutSuccess: () => Promise<void>,
) => {
  try {
    const currentPath = orgSwitchReloadTarget()
    // Read fromOrgName before logoutSuccess clears localStorage.
    const fromOrgName = getLocalAuth()?.organization_name
    // Fire the capture FIRST, while posthog still has the old-session identity.
    // `transport: 'sendBeacon'` asks the browser to guarantee delivery across
    // the upcoming reload, so we don't need a setTimeout race against unload.
    track(
      AnalyticsEvents.ORG_SWITCHED,
      {
        from_org_name: fromOrgName,
        to_org_name: data.organization_name,
        to_region: data.region,
      },
      { transport: 'sendBeacon' },
    )
    await logoutSuccess()
    const userSession = createUserSession(data, data.email)
    loginSuccess(userSession)
    window.location.href = currentPath
  } catch (error) {
    logger.error('Error switching organization', error)
    errorToast({
      title: 'Failed to switch organization',
      description: 'Please refresh the page or sign out and sign back in.',
    })
  }
}

// Switch Organization
export const useSwitchOrganizationMutationV2 = () => {
  const { loginSuccess, logoutSuccess } = useAuth()

  return useMutation({
    mutationFn: async (payload: RSwitchOrganizationPayloadV2) => {
      const response = await settingsService.switchOrganizationV2(payload)
      return response.data
    },
    onSuccess: async (data) => handleOrgSwitchSuccess(data, loginSuccess, logoutSuccess),
    onError: (error) => {
      useAppStore.getState().setSwitchingToOrgName(null)
      const err = getErrorText(error as AxiosError)
      errorToast({
        title: 'Failed to switch organization',
        description: err?.toString() || 'Please try again',
      })
    },
  })
}

export const useProfileMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (profile: RUserProfilePayload) => {
      const response = await settingsService.updateProfile(profile)
      return response.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().profile })
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().users })
    },
  })
}

// Additional email recipients (account-scoped "key contacts")
export const useKeyContactsQuery = () => {
  const { userAuthenticated } = useAuth()
  return useQuery({
    queryKey: getSettingsQueryKeys().keyContacts,
    enabled: userAuthenticated,
    staleTime: convertToMs(5, TimeUnits.Minutes),
    queryFn: async (): Promise<RKeyContacts> => {
      const response = await settingsService.getKeyContacts()
      return response.data
    },
  })
}

export const useKeyContactsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: RKeyContactsPayload) => {
      const response = await settingsService.updateKeyContacts(payload)
      return response.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().keyContacts })
    },
  })
}

// Email Change
export const useRequestEmailChange = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      region: string
      new_email: string
      method: string
      token: string
      session_uuid: string
    }) => authService.requestEmailChange(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().profile })
    },
  })
}

export const useVerifyEmailChange = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { region: string; otp: string; new_email: string }) =>
      authService.verifyEmailChange(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().profile })
    },
  })
}

// Conversation Settings
export const useConversationSettingsQuery = () =>
  useQuery({
    queryKey: getSettingsQueryKeys().conversationsConfiguration,
    staleTime: 0,
    queryFn: async () => {
      const response = await settingsService.getConversationSettings()
      return response.data
    },
  })

export const useConversationSettingsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (settings: RConversationSettings) => {
      const response = await settingsService.updateConversationSettings(settings)
      return response.data
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().conversationsConfiguration,
      }),
  })
}

export const useDestinationsQuery = <TSelect = RDestinationsList>(
  select?: (data: RDestinationsList) => TSelect,
) =>
  useQuery<RDestinationsList, unknown, TSelect>({
    queryKey: getSettingsQueryKeys().destinations,
    queryFn: async () => {
      const response = await settingsService.getDestinations({})
      return response.data
    },
    select,
  })

export const useUpsertDestinationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (destination: RDestination) => {
      const response = await settingsService.upsertDestination(destination)
      return response.data
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().destinations }),
  })
}
export const useDeleteDestinationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => {
      const response = await settingsService.deleteDestination(uuid)
      return response.data
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().destinations }),
  })
}
export const useHolidayGroupsQuery = () =>
  useQuery({
    queryKey: getSettingsQueryKeys().holidayGroups,
    queryFn: () => settingsService.getHolidayGroups(),
    select: (data) => data.data,
  })

export const useHolidayQuery = (holidayGroupId: string | undefined) =>
  useQuery({
    queryKey: ['holidays', holidayGroupId],
    queryFn: async () => {
      if (!holidayGroupId) return null
      const response = await settingsService.getHolidaysList(holidayGroupId)
      return response.data
    },
  })

export const useCreateHolidayMutation = (uuid: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: HolidayFormFields) => {
      const response = await settingsService.createHoliday(payload)
      return response.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().holidayGroups })
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().holidays(uuid) })
    },
  })
}

export const useDeleteHolidayMutation = (holidayGroupId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => {
      const response = await settingsService.deleteHoliday(uuid)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().holidayGroups })
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().holidays(holidayGroupId) })
    },
  })
}

export const useGetAuthIdAndTokenQuery = (type: string | undefined) =>
  useQuery({
    queryKey: getSettingsQueryKeys().getAuthIdAndToken,
    queryFn: () => settingsService.getAuthIdAndToken(),
    // Never in embed: not allow-listed, and the account API token must not auto-fill
    // inside a third-party iframe.
    enabled:
      (type === StartNodeTypes.start_api_request || type === 'auth_details') && !isEmbedMode(),
  })

export const useRegenerateAccountAuthTokenMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ expireTime }: { expireTime: number }) =>
      settingsService.regenerateAccountAuthToken({ expire_time: expireTime }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().getAuthIdAndToken })
    },
  })
}

export const useDeleteAccountAuthTokenMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => settingsService.deleteAccountAuthToken(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().getAuthIdAndToken })
    },
  })
}

export const useDomainsQuery = () => {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: getSettingsQueryKeys().domains,
    queryFn: () => {
      const profile = queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
      const orgId = profile?.organizations[0].org_id
      return hodorService.getDomains(orgId || '')
    },
    select: (data) => data.data,
  })
}

export const useUpsertDomainMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (domain: UpsertDomainPayload) => {
      const profile = queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
      const orgId = profile?.organizations[0].org_id
      const response = await hodorService.upsertDomains(orgId || '', domain)
      return response.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().domains })
    },
    onSuccess: () => {
      successToast({
        title: 'Domain updated',
        description: 'Domain updated successfully',
      })
    },
    onError: (error) => {
      const err = getErrorText(error as AxiosError)
      errorToast({
        title: 'Domain update failed',
        description: err?.toString() || 'Domain update failed',
      })
    },
  })
}

export const useVerifyDomainMutation = (onSuccess: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await hodorService.verifyDomain(token)
      return response.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().domains })
      onSuccess()
    },
    onSuccess: () => {
      successToast({
        title: 'Domain verified',
        description: 'Domain verified successfully',
      })
    },
    onError: (error) => {
      const err = getErrorText(error as AxiosError)
      errorToast({
        title: 'Domain verification failed',
        description: err?.toString() || 'Domain verification failed',
      })
    },
  })
}
